import React, { useEffect, useMemo, useState } from 'react'
import s from './styles/DatePickerInput.module.scss'
import { useDatePickerField } from 'shared/components/DatePickerField/hooks/useDatePickerField'
import Calendar from 'shared/components/Calendar/Calendar'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'

const formatParts = parts => {
	if (!parts || parts.dd === 'dd' || parts.mm === 'mm' || parts.yy === 'yy')
		return ''
	return `${parts.dd}/${parts.mm}/${parts.yy}`
}

const pad2 = v => String(v || '').padStart(2, '0')

const parseTextToISO = text => {
	const value = String(text || '').trim()
	if (!value) return ''

	const match = value.match(/^(\d{1,2})[./-]?(\d{1,2})[./-]?(\d{2}|\d{4})$/)
	if (!match) return null

	let [, ddStr, mmStr, yyStr] = match
	const dd = Number(ddStr)
	const mm = Number(mmStr)
	let year = Number(yyStr)
	if (yyStr.length === 2) year += 2000

	if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(year))
		return null
	if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null

	const d = new Date(year, mm - 1, dd)
	if (
		Number.isNaN(d.getTime()) ||
		d.getFullYear() !== year ||
		d.getMonth() !== mm - 1 ||
		d.getDate() !== dd
	)
		return null

	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

const DatePickerInput = ({
	value,
	onChange,
	minISO,
	className = '',
	inputClassName = '',
}) => {
	const {
		parts,
		isOpen,
		calendarTitle,
		weekdays,
		gridDays,

		pickerRef,
		calendarRef,
		calendarStyle,

		open,
		close,
		prevMonth,
		nextMonth,
		selectDay,
		onKeyDownPicker,

		isSelectedDay,
		isToday,
		isDisabledDay,
	} = useDatePickerField({ valueISO: value, onChange, minISO })

	const displayFromParts = useMemo(() => formatParts(parts), [parts])
	const [text, setText] = useState(displayFromParts)

	useEffect(() => {
		setText(displayFromParts)
	}, [displayFromParts])

	const handleBlur = () => {
		const iso = parseTextToISO(text)
		if (iso === '') {
			onChange?.('')
			setText('')
			return
		}
		if (!iso) {
			setText(displayFromParts)
			return
		}
		if (minISO && iso < minISO) {
			setText(displayFromParts)
			return
		}
		onChange?.(iso)
	}

	return (
		<div className={`${s.root} ${className}`.trim()}>
			<input
				ref={pickerRef}
				type="text"
				className={`${s.input} ${inputClassName}`.trim()}
				value={text}
				onChange={e => setText(e.target.value)}
				onFocus={open}
				onClick={open}
				onKeyDown={onKeyDownPicker}
				onBlur={handleBlur}
				placeholder="dd/mm/yy"
				aria-label="Date"
			/>
			<button
				type="button"
				className={s.calendarBtn}
				onMouseDown={e => e.preventDefault()}
				onClick={open}
				aria-label="Open calendar"
			>
				<CalendarIcon />
			</button>

			<Calendar
				isOpen={isOpen}
				style={calendarStyle}
				calendarRef={calendarRef}
				title={calendarTitle}
				weekdays={weekdays}
				gridDays={gridDays}
				onPrev={prevMonth}
				onNext={nextMonth}
				onSelectDay={selectDay}
				onClose={close}
				isSelectedDay={isSelectedDay}
				isToday={isToday}
				isDisabledDay={isDisabledDay}
			/>
		</div>
	)
}

export default DatePickerInput
